import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { DailyTeam, TeamDailyPerson } from "@msc/shared";

import { useDailyTeamSchreiben } from "../../hooks/useTeamDaily.js";

/**
 * Teams eines Bereichs pflegen: anlegen, umbenennen, Mitglieder setzen,
 * löschen.
 *
 * **Jeder, der den Bereich sieht, darf das** (Entscheidung Florian Fuchs,
 * 2026-10-01). Ein Daily-Team ist Arbeitsorganisation, keine Stammdaten — wer
 * morgens vor dem Board steht, stellt es sich selbst zusammen. Der Server
 * zieht dieselbe Grenze über die Bereichs-Reichweite.
 *
 * **Löschen nimmt die Notizen mit.** Das steht im Dialog, nicht erst im
 * Nachhinein: Ein Zettel ohne Team gehört niemandem mehr, und das ist der
 * einzige Schritt hier, der etwas vernichtet.
 */
export function TeamVerwaltung({
  opened,
  onClose,
  segmentId,
  teams,
  personen,
  aktivesTeam,
  onTeamGewaehlt,
}: {
  opened: boolean;
  onClose: () => void;
  segmentId: string;
  teams: DailyTeam[];
  personen: TeamDailyPerson[];
  aktivesTeam: string | null;
  /** Nach dem Anlegen zeigt das Board gleich das neue Team. */
  onTeamGewaehlt: (teamId: string | null) => void;
}) {
  const { t } = useTranslation();
  const { anlegen, aendern, loeschen } = useDailyTeamSchreiben(segmentId);

  /** `null` = nichts offen, `"neu"` = Anlegeformular, sonst die Team-Kennung. */
  const [offen, setOffen] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null);

  const auswahl = personen.map((p) => ({ value: p.employeeId, label: p.fullName }));

  const formularSchliessen = () => {
    setOffen(null);
    setName("");
    setMemberIds([]);
    setFehler(null);
  };

  const bearbeiten = (team: DailyTeam) => {
    setOffen(team.id);
    setName(team.name);
    setMemberIds(team.members.map((m) => m.employeeId));
    setFehler(null);
  };

  const neu = () => {
    setOffen("neu");
    setName("");
    setMemberIds([]);
    setFehler(null);
  };

  const speichern = async () => {
    setFehler(null);
    const input = { name: name.trim(), memberIds };
    try {
      if (offen === "neu") {
        const res = await anlegen.mutateAsync(input);
        onTeamGewaehlt(res.team.id);
      } else if (offen) {
        await aendern.mutateAsync({ id: offen, input });
      }
      formularSchliessen();
    } catch (err) {
      setFehler((err as Error).message);
    }
  };

  const entfernen = async (teamId: string) => {
    setFehler(null);
    try {
      await loeschen.mutateAsync(teamId);
      if (aktivesTeam === teamId) onTeamGewaehlt(null);
      setLoeschFrage(null);
    } catch (err) {
      setFehler((err as Error).message);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("pages.teamDaily.teamsManage")}
      size="lg"
    >
      <Stack gap="md">
        {fehler && (
          <Alert color="red" variant="light">
            {fehler}
          </Alert>
        )}

        {teams.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("pages.teamDaily.teamsEmpty")}
          </Text>
        ) : (
          <Table>
            <Table.Tbody>
              {teams.map((team) => (
                <Table.Tr key={team.id}>
                  <Table.Td>
                    <Text fw={500}>{team.name}</Text>
                    <Text size="xs" c="dimmed">
                      {team.members.map((m) => m.fullName).join(", ") ||
                        t("pages.teamDaily.teamNoMembers")}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ width: 1, whiteSpace: "nowrap" }}>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => bearbeiten(team)}
                      >
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        onClick={() => setLoeschFrage(team.id)}
                      >
                        {t("common.delete")}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {loeschFrage && (
          <Alert color="red" variant="light" title={t("pages.teamDaily.teamDeleteTitle")}>
            <Stack gap="xs">
              <Text size="sm">{t("pages.teamDaily.teamDeleteBody")}</Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  color="red"
                  loading={loeschen.isPending}
                  onClick={() => void entfernen(loeschFrage)}
                >
                  {t("common.delete")}
                </Button>
                <Button size="xs" variant="default" onClick={() => setLoeschFrage(null)}>
                  {t("common.cancel")}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {offen === null ? (
          <Group>
            <Button size="xs" onClick={neu}>
              {t("pages.teamDaily.teamNew")}
            </Button>
          </Group>
        ) : (
          <Stack gap="sm">
            <TextInput
              label={t("pages.teamDaily.teamName")}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              maxLength={60}
            />
            <MultiSelect
              label={t("pages.teamDaily.teamMembers")}
              data={auswahl}
              value={memberIds}
              onChange={setMemberIds}
              searchable
              clearable
            />
            <Group gap="xs">
              <Button
                size="xs"
                onClick={() => void speichern()}
                disabled={name.trim().length === 0}
                loading={anlegen.isPending || aendern.isPending}
              >
                {t("common.save")}
              </Button>
              <Button size="xs" variant="default" onClick={formularSchliessen}>
                {t("common.cancel")}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
